import {
    infiniteQueryOptions,
    queryOptions,
    useInfiniteQuery,
    useQuery,
} from "@tanstack/react-query";
import { createServerFn } from "@tanstack/react-start";
import type { Review } from "@/components/shared/ReviewsSection";
import { getCatalog } from "@/lib/core/catalog-core.server";
import {
    ProviderReviewsInputSchema,
    ProviderReviewsPageSchema,
} from "./provider.contract";

const getWagsOrgReviews = createServerFn({ method: "GET" })
    .inputValidator((data) => ProviderReviewsInputSchema.parse(data))
    .handler(async ({ data }) => {
        const catalog = await getCatalog();
        const page = await catalog.organizations.getOrganizationReviews(data);
        return ProviderReviewsPageSchema.parse({
            reviews: page.reviews.map((review) => ({
                id: review.id,
                organizationId: review.organizationId,
                customerName: review.customerName,
                description: review.description,
                createdAt: review.createdAt?.toISOString() ?? null,
                customerPhotoUrl: review.customerPhotoUrl,
                rating: review.rating,
            })),
            total: page.total,
        });
    });

const reviewsQueryOptions = (organizationId: string, limit: number) =>
    queryOptions({
        queryKey: ["wagsOrgReviews", organizationId, limit],
        queryFn: () =>
            getWagsOrgReviews({ data: { organizationId, limit, offset: 0 } }),
        enabled: Boolean(organizationId),
        staleTime: 60_000,
    });

export const useWagsOrganizationsReviewsLiveQuery = (
    organizationId: string,
    { limit = 10 }: { limit?: number } = {},
) => useQuery(reviewsQueryOptions(organizationId, limit));

const REVIEWS_PAGE_SIZE = 50;

export const providerReviewsPagesQueryOptions = (organizationId: string) =>
    infiniteQueryOptions({
        queryKey: ["wagsOrgReviewsPages", organizationId, REVIEWS_PAGE_SIZE],
        queryFn: ({ pageParam }) =>
            getWagsOrgReviews({
                data: {
                    organizationId,
                    limit: REVIEWS_PAGE_SIZE,
                    offset: pageParam * REVIEWS_PAGE_SIZE,
                },
            }),
        initialPageParam: 0,
        getNextPageParam: (lastPage, pages) =>
            lastPage.reviews.length > 0 &&
            pages.length * REVIEWS_PAGE_SIZE < lastPage.total
                ? pages.length
                : undefined,
        staleTime: 60_000,
    });

export const useProviderReviewsPages = (organizationId: string) =>
    useInfiniteQuery(providerReviewsPagesQueryOptions(organizationId));

export function reviewCountLabel(count: number) {
    return `${count.toLocaleString()} ${count === 1 ? "review" : "reviews"}`;
}

export type WagsOrgReview = Awaited<
    ReturnType<typeof getWagsOrgReviews>
>["reviews"][number];

const relativeTimeFormatter = new Intl.RelativeTimeFormat("en", {
    numeric: "auto",
});

export function mapOrgReviewToReview(
    r: WagsOrgReview,
    dogName = "a puppy",
): Review {
    return {
        id: r.id,
        author: r.customerName ?? "Anonymous",
        photoUrl: r.customerPhotoUrl,
        timeAgo: (() => {
            if (r.createdAt === null) return "";
            const diffMs = Date.now() - new Date(r.createdAt).getTime();
            if (!Number.isFinite(diffMs)) return "";
            return relativeTimeFormatter.format(
                -Math.round(diffMs / (1000 * 60 * 60 * 24 * 30)),
                "months",
            );
        })(),
        text: r.description ?? "",
        dogName,

        rating: r.rating,
    };
}
